import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ArrowRight,
  Ban,
  Calendar,
  CheckCircle2,
  Circle,
  MoreVertical,
  PlayCircle,
} from "lucide-react";

import { Avatar } from "../../components/Avatar";
import { Badge } from "../../components/Badge";
import { ReadOnlyBadge } from "../../components/ReadOnlyBadge";
import { SetupProgress } from "../../components/SetupProgress";
import { Button } from "../../components/Button";
import {
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../components/DropdownMenu";
import { DataTable } from "../../components/DataTable";
import { SetupIcon } from "../../components/icons/SetupIcon";
import { getAvatarImageUrl } from "../../utils/avatarUtils";
import { useAuth } from "../../components/contexts/AuthContext";
import { canSeeContractedPack } from "@/utils/agencyFeatures";
import { isClientReadOnly } from "@/utils/clientAccess";

const TABLE_COLUMNS = [
  { key: "client", label: "Cliente", tooltip: "Cliente asociado al setup" },
  { key: "state", label: "Estado", tooltip: "Estado actual del setup" },
  { key: "pack", label: "Pack", tooltip: "Pack de setup contratado" },
  { key: "start", label: "Inicio", tooltip: "Fecha de inicio del setup" },
  { key: "progress", label: "Progreso", tooltip: "Porcentaje de avance del setup" },
  { key: "actions", label: "Acciones", tooltip: "Acciones disponibles para el setup" },
];

const setupStateColorMap = {
  completed: "green",
  in_progress: "yellow",
  not_started: "blue",
  cancelled: "red",
  no_necesario: "gray",
};

const setupStateLabels = {
  completed: "Completado",
  in_progress: "En progreso",
  not_started: "No iniciado",
  cancelled: "Cancelado",
  no_necesario: "No necesario",
};

const formatDate = (value) => {
  if (!value) return "Sin fecha";

  try {
    return new Date(value).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
};

function SetupActionsCell({ project, onStateChange, onViewProgress }) {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      spacing={1}
      onClick={(event) => event.stopPropagation()}
    >
      <Tooltip title="Ver progreso del setup" arrow>
        <Box>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewProgress(project)}
            sx={{ whiteSpace: "nowrap" }}
          >
            <ArrowRight size={14} />
            Progreso
          </Button>
        </Box>
      </Tooltip>

      {onStateChange && (
        <>
          <Tooltip title="Ver acciones" arrow>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{
                width: "28px",
                height: "28px",
                padding: 0,
                flexShrink: 0,
              }}
            >
              <MoreVertical size={16} />
            </IconButton>
          </Tooltip>

          <DropdownMenuContent
            anchorEl={menuAnchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            align="end"
            onClick={(event) => event.stopPropagation()}
          >
            {project.state !== "in_progress" && (
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleMenuClose();
                  onStateChange(project.id, "in_progress", project.state);
                }}
              >
                <PlayCircle size={16} />
                En progreso
              </DropdownMenuItem>
            )}

            {project.state !== "completed" && (
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleMenuClose();
                  onStateChange(project.id, "completed", project.state);
                }}
              >
                <CheckCircle2 size={16} />
                Completado
              </DropdownMenuItem>
            )}

            {project.state !== "not_started" && (
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleMenuClose();
                  onStateChange(project.id, "not_started", project.state);
                }}
              >
                <Circle size={16} />
                No iniciado
              </DropdownMenuItem>
            )}

            {project.state !== "no_necesario" && (
              <DropdownMenuItem
                onClick={(event) => {
                  event.stopPropagation();
                  handleMenuClose();
                  onStateChange(project.id, "no_necesario", project.state);
                }}
              >
                <Ban size={16} />
                No necesario
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </>
      )}
    </Stack>
  );
}

const captionSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  fontSize: { xs: "0.7rem", md: "0.75rem" },
  whiteSpace: "nowrap",
};

export function TechnicalSetupTableView({
  rows,
  totalCount,
  isFetching,
  currentPage,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onViewProgress,
  onStateChange,
  toolbarProps,
}) {
  const theme = useTheme();
  const { user: currentUser } = useAuth();
  const showPack = canSeeContractedPack(currentUser);

  const columns = useMemo(() => {
    const renderers = {
      client: (project) => (
        <Tooltip title={project.client_name || "Sin cliente"} arrow>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
            <Avatar
              size="sm"
              name={project.client_name || "Cliente"}
              src={getAvatarImageUrl(project.client_logo) || undefined}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: { xs: "0.75rem", md: "0.875rem" },
              }}
            >
              {project.client_name || "Sin cliente"}
            </Typography>
          </Box>
        </Tooltip>
      ),
      state: (project) => (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Badge color={setupStateColorMap[project.state] || "gray"} size="sm">
            {setupStateLabels[project.state] || project.state || "-"}
          </Badge>
          {isClientReadOnly(currentUser, project.client) && <ReadOnlyBadge />}
        </Stack>
      ),
      pack: (project) => (
        <Tooltip title={project.setup_variant_display || "Ningún pack contratado"} arrow>
          <Typography
            variant="caption"
            sx={{
              ...captionSx,
              color: project.setup_variant_display
                ? theme.palette.text.secondary
                : theme.palette.error.main,
              fontWeight: project.setup_variant_display ? 400 : 500,
            }}
          >
            <SetupIcon size={12} />
            {project.setup_variant_display || "Ningún pack contratado"}
          </Typography>
        </Tooltip>
      ),
      start: (project) => (
        <Tooltip title={formatDate(project.start_date)} arrow>
          <Typography variant="caption" sx={captionSx}>
            <Calendar size={12} color={theme.palette.text.secondary} />
            {formatDate(project.start_date)}
          </Typography>
        </Tooltip>
      ),
      progress: (project) => (
        <SetupProgress
          value={project.progress_percentage}
          completed={project.state === "completed"}
        />
      ),
      actions: (project) => (
        <SetupActionsCell
          project={project}
          onStateChange={
            isClientReadOnly(currentUser, project.client) ? undefined : onStateChange
          }
          onViewProgress={onViewProgress}
        />
      ),
    };

    return TABLE_COLUMNS.filter((column) => showPack || column.key !== "pack").map((column) => ({
      key: column.key,
      header: column.label,
      tooltip: column.tooltip,
      align: column.key === "actions" ? "right" : "left",
      render: renderers[column.key],
      ...(column.key === "client" && { minWidth: { xs: 220, md: 280 }, width: { md: 280 } }),
    }));
  }, [theme, currentUser, onStateChange, onViewProgress, showPack]);

  return (
    <DataTable
      {...toolbarProps}
      columns={columns}
      rows={rows}
      loading={isFetching}
      emptyIcon={Calendar}
      emptyMessage="No hay proyectos de setup"
      onRowClick={(project) => onViewProgress(project)}
      tableMinWidth={980}
      maxHeight="calc(100vh - 290px)"
      minHeight="calc(100vh - 290px)"
      page={currentPage}
      rowsPerPage={rowsPerPage}
      totalCount={totalCount}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
    />
  );
}

SetupActionsCell.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    state: PropTypes.string,
  }).isRequired,
  onStateChange: PropTypes.func,
  onViewProgress: PropTypes.func.isRequired,
};

TechnicalSetupTableView.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  totalCount: PropTypes.number.isRequired,
  isFetching: PropTypes.bool,
  currentPage: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onViewProgress: PropTypes.func.isRequired,
  onStateChange: PropTypes.func,
  toolbarProps: PropTypes.object,
};
